import { useEffect } from 'react';

/**
 * Global designed tooltips (port of the kit): any element with data-tooltip="…" (optional data-shortcut="F"),
 * and any icon-only button/link with an aria-label. Hover (350ms), keyboard focus, or touch long-press (500ms).
 * Native title="" attributes are converted so the browser tooltip never doubles up.
 */
export function TooltipLayer(): null {
  useEffect(() => {
    let tip: HTMLDivElement | null = null;
    let cur: HTMLElement | null = null;
    let timer = 0;
    const textOf = (el: HTMLElement): string | null => {
      if (el.dataset.tooltip) return el.dataset.tooltip;
      const iconOnly = el.matches('button, a, [role=button]') && !el.textContent?.trim() && !!el.querySelector('svg, img');
      return iconOnly ? el.getAttribute('aria-label') : null;
    };
    const target = (n: EventTarget | null): HTMLElement | null => {
      let el = n instanceof Element ? n.closest<HTMLElement>('[data-tooltip], button, a, [role=button], [title]') : null;
      while (el) {
        const title = el.getAttribute('title');
        if (title && !el.dataset.tooltip) {
          if (!el.getAttribute('aria-label') && !el.textContent?.trim()) el.setAttribute('aria-label', title);
          el.dataset.tooltip = title;
          el.removeAttribute('title');
        }
        if (textOf(el)) return el;
        el = el.parentElement?.closest<HTMLElement>('[data-tooltip], button, a, [role=button], [title]') ?? null;
      }
      return null;
    };
    const hide = () => {
      window.clearTimeout(timer);
      cur?.removeAttribute('aria-describedby');
      cur = null;
      if (tip) {
        const t = tip;
        tip = null;
        t.classList.remove('in');
        window.setTimeout(() => t.remove(), 120);
      }
    };
    const show = (el: HTMLElement) => {
      const text = textOf(el);
      if (!text) return;
      hide();
      cur = el;
      tip = document.createElement('div');
      tip.className = 'ui-tip';
      tip.id = 'ui-tip';
      tip.setAttribute('role', 'tooltip');
      tip.textContent = text;
      if (el.dataset.shortcut) {
        const k = document.createElement('kbd');
        k.textContent = el.dataset.shortcut;
        tip.append(k);
      }
      document.body.append(tip);
      el.setAttribute('aria-describedby', 'ui-tip');
      const r = el.getBoundingClientRect();
      const vw = document.documentElement.clientWidth;
      const left = Math.min(Math.max(8, r.left + r.width / 2 - tip.offsetWidth / 2), vw - tip.offsetWidth - 8);
      let top = r.top - tip.offsetHeight - 8;
      tip.dataset.place = 'top';
      if (top < 8) { top = r.bottom + 8; tip.dataset.place = 'bottom'; }
      tip.style.left = `${left}px`;
      tip.style.top = `${top}px`;
      tip.style.setProperty('--ax', `${Math.min(Math.max(10, r.left + r.width / 2 - left), tip.offsetWidth - 10)}px`);
      const t = tip;
      requestAnimationFrame(() => t.classList.add('in'));
    };
    const over = (e: PointerEvent) => {
      if (e.pointerType === 'touch') return;
      const el = target(e.target);
      if (el === cur) return;
      hide();
      if (el) timer = window.setTimeout(() => show(el), 350);
    };
    const out = (e: PointerEvent) => {
      if (cur && !cur.contains(e.relatedTarget as Node | null)) hide();
      else if (!cur) window.clearTimeout(timer);
    };
    const focusIn = (e: FocusEvent) => {
      if (e.target instanceof HTMLElement && e.target.matches(':focus-visible')) {
        const el = target(e.target);
        if (el) show(el);
      }
    };
    const down = (e: PointerEvent) => {
      hide();
      if (e.pointerType === 'touch') {
        const el = target(e.target);
        if (el) timer = window.setTimeout(() => show(el), 500);
      }
    };
    const up = (e: PointerEvent) => {
      if (e.pointerType === 'touch') { window.clearTimeout(timer); if (tip) window.setTimeout(hide, 1200); }
    };
    const key = (e: KeyboardEvent) => e.key === 'Escape' && hide();
    document.addEventListener('pointerover', over);
    document.addEventListener('pointerout', out);
    document.addEventListener('focusin', focusIn);
    document.addEventListener('focusout', hide);
    document.addEventListener('pointerdown', down, true);
    document.addEventListener('pointerup', up, true);
    document.addEventListener('keydown', key);
    window.addEventListener('scroll', hide, true);
    return () => {
      hide();
      document.removeEventListener('pointerover', over);
      document.removeEventListener('pointerout', out);
      document.removeEventListener('focusin', focusIn);
      document.removeEventListener('focusout', hide);
      document.removeEventListener('pointerdown', down, true);
      document.removeEventListener('pointerup', up, true);
      document.removeEventListener('keydown', key);
      window.removeEventListener('scroll', hide, true);
    };
  }, []);
  return null;
}
